import { Injectable, inject, signal } from '@angular/core';
import { Observable, of, delay, timer, map } from 'rxjs';
import {
  ApiEnvelope,
  ApiErrorCodes,
  AuthenticateSuccessData,
  OtpSendData,
  OtpVerifySuccessData,
  VinDecodeData,
  VinEligibilityData,
  VinCommitData,
  VinRequestStatusData,
  VinAddStatus,
  SubmitExceptionResponseData,
  AdminContractSearchData,
  AdminContractDetailData,
  AdminRequestDetailData,
  AdminNoteResponseData,
  AdminLoginResponseData,
  AdminUserListData,
  AdminUser,
  AdminUserCreateRequest,
  AdminUserUpdateRequest,
  AdminUserResponseData,
  AdminUserDeleteData,
  AdminAuditEventListData,
  AdminAuditEventRecord,
  AdminAuditFilterKey,
  VinDecoded,
} from '@core/models';
import { SessionService } from './session.service';
import { isSameAsPrimaryVin } from '@shared/utils/is-same-as-primary-vin';

/**
 * Mock API Service for development and testing
 * Simulates backend responses with realistic delays
 */
@Injectable({ providedIn: 'root' })
export class MockApiService {
  private readonly sessionService = inject(SessionService);

  // Simulated database state
  private readonly _contracts = signal<Map<string, MockContract>>(new Map());
  private readonly _requests = signal<Map<string, MockVinRequest>>(new Map());
  private readonly _otpChallenges = signal<Map<string, MockOtpChallenge>>(new Map());

  // Track authentication attempts for rate limiting simulation
  private authAttempts = 0;
  private lastAuthAttemptTime = 0;

  constructor() {
    this.initializeMockData();
  }

  // ============== Contract Authentication ==============

  authenticateContract(
    vin7: string,
    lastName: string,
    zip: string,
  ): Observable<ApiEnvelope<AuthenticateSuccessData>> {
    return of(null).pipe(
      delay(this.randomDelay(500, 1500)),
      map(() => {
        // Simulate rate limiting
        const now = Date.now();
        if (now - this.lastAuthAttemptTime < 1000) {
          this.authAttempts++;
          if (this.authAttempts > 5) {
            return this.errorResponse<AuthenticateSuccessData>(
              ApiErrorCodes.RATE_LIMITED,
              'Too many attempts. Please wait.',
              { retryAfterSeconds: 60 },
            );
          }
        } else {
          this.authAttempts = 1;
        }
        this.lastAuthAttemptTime = now;

        // Check for valid test credentials
        const validCredentials: Record<string, { lastName: string; zip: string }> = {
          '1234567': { lastName: 'SMITH', zip: '30301' },
          '7654321': { lastName: 'JONES', zip: '10001' },
          '0TP7654': { lastName: 'TESTUSER', zip: '12345' },
          LOCKED1: { lastName: 'LOCKED', zip: '99999' },
          A234567: { lastName: 'GARCIA', zip: '90210' },
          B345678: { lastName: 'WILLIAMS', zip: '60601' },
          C456789: { lastName: 'JOHNSON', zip: '33101' },
          D567890: { lastName: 'MARTINEZ', zip: '75201' },
          E678901: { lastName: 'TAYLOR', zip: '98101' },
        };

        const normalized = vin7.toUpperCase();
        const expected = validCredentials[normalized];
        if (!expected) {
          return this.errorResponse<AuthenticateSuccessData>(
            ApiErrorCodes.AUTH_NO_MATCH,
            'No contract found matching the provided information.',
          );
        }

        if (lastName.toUpperCase() !== expected.lastName || zip !== expected.zip) {
          return this.errorResponse<AuthenticateSuccessData>(
            ApiErrorCodes.AUTH_NO_MATCH,
            'No contract found matching the provided information.',
          );
        }

        // Check if contract requires OTP
        if (normalized === '0TP7654' || normalized === 'C456789') {
          const challengeId = this.generateId('otp');
          this._otpChallenges.update((m) => {
            m.set(challengeId, {
              id: challengeId,
              contractContextId: 'ctx-otp',
              code: '123456',
              expiresAt: new Date(Date.now() + 10 * 60 * 1000),
              attempts: 0,
            });
            return new Map(m);
          });

          return this.errorResponse<AuthenticateSuccessData>(
            ApiErrorCodes.AUTH_OTP_REQUIRED,
            'OTP verification required.',
            {
              contractContextId: 'ctx-otp',
              otpChallengeId: challengeId,
              maskedDestination: '***-***-1234',
              channel: 'sms',
            },
          );
        }

        // Check if contract is locked — Lithia exception path returns session details in error payload.
        if (normalized === 'LOCKED1' || normalized === 'E678901') {
          const contractContextId = `ctx-${normalized}`;
          const contract = this.getOrCreateContract(contractContextId, normalized);
          contract.hasAdditionalVin = true;

          return this.errorResponse<AuthenticateSuccessData>(
            ApiErrorCodes.CONTRACT_LOCKED,
            'This contract already has an additional vehicle registered.',
            {
              contractContextId,
              sessionToken: this.generateMockSessionToken(contractContextId),
              sessionExpiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
              contractSummary: {
                primaryVinMasked: contract.primaryVinMasked,
                hasAdditionalVin: true,
              },
              exceptionEligible: true,
            },
          );
        }

        // Successful authentication
        const contractContextId = `ctx-${normalized}`;
        const contract = this.getOrCreateContract(contractContextId, normalized);

        return this.successResponse<AuthenticateSuccessData>({
          contractContextId,
          sessionToken: this.generateMockSessionToken(contractContextId),
          sessionExpiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          otp: {
            status: 'NOT_REQUIRED',
            otpChallengeId: null,
            maskedDestination: null,
            channel: null,
          },
          contractSummary: {
            primaryVinMasked: contract.primaryVinMasked,
            hasAdditionalVin: contract.hasAdditionalVin,
          },
        });
      }),
    );
  }

  // ============== OTP ==============

  sendOtp(otpChallengeId: string): Observable<ApiEnvelope<OtpSendData>> {
    return of(null).pipe(
      delay(this.randomDelay(300, 800)),
      map(() => {
        const challenge = this._otpChallenges().get(otpChallengeId);
        if (!challenge) {
          return this.errorResponse<OtpSendData>(
            ApiErrorCodes.OTP_EXPIRED,
            'OTP challenge not found or expired.',
          );
        }

        // Regenerate code
        challenge.code = '123456'; // Fixed for testing
        challenge.expiresAt = new Date(Date.now() + 10 * 60 * 1000);

        return this.successResponse<OtpSendData>({
          otpChallengeId,
          status: 'SENT',
          expiresAt: challenge.expiresAt.toISOString(),
        });
      }),
    );
  }

  verifyOtp(otpChallengeId: string, code: string): Observable<ApiEnvelope<OtpVerifySuccessData>> {
    return of(null).pipe(
      delay(this.randomDelay(400, 1000)),
      map(() => {
        const challenge = this._otpChallenges().get(otpChallengeId);
        if (!challenge) {
          return this.errorResponse<OtpVerifySuccessData>(
            ApiErrorCodes.OTP_EXPIRED,
            'OTP challenge not found or expired.',
          );
        }

        if (challenge.expiresAt < new Date()) {
          return this.errorResponse<OtpVerifySuccessData>(
            ApiErrorCodes.OTP_EXPIRED,
            'OTP code has expired.',
          );
        }

        challenge.attempts++;
        if (challenge.attempts > 3) {
          return this.errorResponse<OtpVerifySuccessData>(
            ApiErrorCodes.OTP_LOCKED_OUT,
            'Too many failed attempts.',
          );
        }

        if (code !== challenge.code) {
          return this.errorResponse<OtpVerifySuccessData>(
            ApiErrorCodes.OTP_INVALID,
            'Invalid OTP code.',
          );
        }

        // Success
        return this.successResponse<OtpVerifySuccessData>({
          contractContextId: challenge.contractContextId,
          sessionToken: this.generateMockSessionToken(challenge.contractContextId),
          sessionExpiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          otp: {
            status: 'VERIFIED',
            otpChallengeId,
            maskedDestination: '***-***-1234',
            channel: 'sms',
          },
        });
      }),
    );
  }

  // ============== VIN Operations ==============

  decodeVin(vin: string): Observable<ApiEnvelope<VinDecodeData>> {
    return of(null).pipe(
      delay(this.randomDelay(300, 700)),
      map(() => {
        const normalized = vin.toUpperCase().replace(/\s/g, '');

        // Invalid format
        if (normalized.length !== 17 || /[IOQ]/.test(normalized)) {
          return this.errorResponse<VinDecodeData>(
            ApiErrorCodes.VIN_INVALID_FORMAT,
            'Invalid VIN format.',
          );
        }

        // Mock VIN database
        const mockVins: Record<string, VinDecoded> = {
          '1HGCM82633A123456': { year: 2003, make: 'Honda', model: 'Accord' },
          '1HGCM56787A123456': { year: 2007, make: 'Honda', model: 'Civic' },
          '5FNRL38437B123456': { year: 2007, make: 'Honda', model: 'Odyssey' },
          WVWZZZ3CZWE123456: { year: 2022, make: 'Volkswagen', model: 'Golf' },
          '1G1YY22G965123456': { year: 2006, make: 'Chevrolet', model: 'Corvette' },
          '3N1AB7AP5KY123456': { year: 2019, make: 'Nissan', model: 'Sentra' },
        };

        const decoded = mockVins[normalized];
        if (!decoded) {
          // Generate random decode for unknown VINs
          const randomDecoded: VinDecoded = {
            year: 2020 + Math.floor(Math.random() * 5),
            make: ['Toyota', 'Ford', 'Chevrolet', 'Honda'][Math.floor(Math.random() * 4)],
            model: ['Sedan', 'SUV', 'Truck', 'Coupe'][Math.floor(Math.random() * 4)],
          };
          return this.successResponse<VinDecodeData>({ vin: normalized, decoded: randomDecoded });
        }

        return this.successResponse<VinDecodeData>({ vin: normalized, decoded });
      }),
    );
  }

  checkEligibility(vin: string): Observable<ApiEnvelope<VinEligibilityData>> {
    return of(null).pipe(
      delay(this.randomDelay(400, 1000)),
      map(() => {
        const normalized = vin.toUpperCase().replace(/\s/g, '');

        const authPrimaryVin = this.sessionService.getAuthFormValues()?.vin;
        if (authPrimaryVin && isSameAsPrimaryVin(normalized, authPrimaryVin)) {
          return this.successResponse<VinEligibilityData>({
            vin: normalized,
            eligible: false,
            reasonCode: 'SAME_AS_PRIMARY',
            message: 'The additional vehicle VIN cannot be the same as the primary vehicle VIN.',
            requestId: `mock-failed-req-same-as-primary-${Date.now()}`,
          });
        }

        // Simulate different eligibility scenarios
        if (normalized === '1G1YY22G965123456') {
          // Corvette - too high class
          return this.successResponse<VinEligibilityData>({
            vin: normalized,
            eligible: false,
            reasonCode: 'CLASS_TOO_LOW',
            message: 'secondaryVin class lower than primaryVin',
            primaryVin: { make: 'Audi', model: 'R8', class: '3' },
            secondaryVin: { make: 'Chevrolet', model: 'Corvette', class: '1' },
            requestId: 'mock-failed-req-class-too-high',
          });
        }

        if (normalized.startsWith('5FNRL')) {
          // Odyssey - already used
          return this.successResponse<VinEligibilityData>({
            vin: normalized,
            eligible: false,
            reasonCode: 'VIN_ALREADY_USED',
            requestId: 'mock-failed-req-vin-used',
          });
        }

        if (normalized.endsWith('000001')) {
          return this.successResponse<VinEligibilityData>({
            vin: normalized,
            eligible: false,
            reasonCode: 'WINDOW_EXPIRED',
          });
        }

        if (normalized.endsWith('000002')) {
          return this.successResponse<VinEligibilityData>({
            vin: normalized,
            eligible: false,
            reasonCode: 'CLASS_NOT_FOUND',
          });
        }

        // All others are eligible
        return this.successResponse<VinEligibilityData>({
          vin: normalized,
          eligible: true,
          reasonCode: 'OK',
        });
      }),
    );
  }

  submitException(
    _secondaryVin: string,
    _customerFirstName: string,
    _customerLastName: string,
    _customerEmail: string,
    _vinAddRequestId?: string,
  ): Observable<ApiEnvelope<SubmitExceptionResponseData>> {
    return of(null).pipe(
      delay(this.randomDelay(400, 900)),
      map(() => {
        const id = `exc-mock-${Date.now()}`;
        const caseRef = `EXC-${id.slice(-8).toUpperCase()}`;
        return this.successResponse<SubmitExceptionResponseData>({
          id,
          caseRef,
          salesforceCaseId: `500Hs00000${caseRef.replace(/[^0-9]/g, '').padStart(6, '0')}`,
          status: 'PENDING',
        });
      }),
    );
  }

  commitVin(
    vin: string,
    acceptIrreversible: boolean,
    idempotencyKey: string,
    _customerFirstName?: string,
    _customerLastName?: string,
    _customerEmail?: string,
  ): Observable<ApiEnvelope<VinCommitData>> {
    return of(null).pipe(
      delay(this.randomDelay(800, 2000)),
      map(() => {
        if (!acceptIrreversible) {
          return this.errorResponse<VinCommitData>(
            ApiErrorCodes.INTERNAL_ERROR,
            'Must accept irreversible terms.',
          );
        }

        const normalized = vin.toUpperCase().replace(/\s/g, '');
        const requestId = `req-${Date.now()}`;

        // Check for idempotent retry
        const existingRequest = Array.from(this._requests().values()).find(
          (r) => r.idempotencyKey === idempotencyKey,
        );

        if (existingRequest) {
          if (existingRequest.status === VinAddStatus.FAILED_INELIGIBLE) {
            return this.errorResponse<VinCommitData>(
              ApiErrorCodes.VIN_INELIGIBLE,
              `VIN is not eligible: ${existingRequest.eligibilityReasonCode ?? 'UNKNOWN'}`,
              {
                requestId: existingRequest.id,
                reasonCode: existingRequest.eligibilityReasonCode ?? 'UNKNOWN',
              },
            );
          }
          return this.successResponse<VinCommitData>({
            requestId: existingRequest.id,
            status: existingRequest.status,
            vin: existingRequest.vin,
            decoded: existingRequest.decoded,
          });
        }

        const decoded: VinDecoded = {
          year: 2023,
          make: 'Test',
          model: 'Vehicle',
        };

        const exceptionReason = normalized.endsWith('000001')
          ? 'WINDOW_EXPIRED'
          : normalized.endsWith('000002')
            ? 'CLASS_NOT_FOUND'
            : null;

        if (exceptionReason) {
          const request: MockVinRequest = {
            id: requestId,
            contractContextId: 'ctx-001',
            vin: normalized,
            decoded,
            status: VinAddStatus.FAILED_INELIGIBLE,
            eligibilityReasonCode: exceptionReason,
            idempotencyKey,
            createdAt: new Date(),
            lastUpdatedAt: new Date(),
          };

          this._requests.update((m) => {
            m.set(requestId, request);
            return new Map(m);
          });

          return this.errorResponse<VinCommitData>(
            ApiErrorCodes.VIN_INELIGIBLE,
            `VIN is not eligible: ${exceptionReason}`,
            { requestId, reasonCode: exceptionReason },
          );
        }

        // Simulate random PENDING vs immediate success (80% immediate success)
        const status = Math.random() < 0.8 ? VinAddStatus.COMMITTED_LOCKED : VinAddStatus.PENDING;

        const request: MockVinRequest = {
          id: requestId,
          contractContextId: 'ctx-001',
          vin: normalized,
          decoded,
          status,
          idempotencyKey,
          createdAt: new Date(),
          lastUpdatedAt: new Date(),
        };

        this._requests.update((m) => {
          m.set(requestId, request);
          return new Map(m);
        });

        // If PENDING, simulate async completion after a few seconds
        if (status === VinAddStatus.PENDING) {
          timer(5000).subscribe(() => {
            this._requests.update((m) => {
              const req = m.get(requestId);
              if (req && req.status === VinAddStatus.PENDING) {
                req.status = VinAddStatus.COMMITTED_LOCKED;
                req.lastUpdatedAt = new Date();
              }
              return new Map(m);
            });
          });
        }

        return this.successResponse<VinCommitData>({
          requestId,
          status,
          vin: normalized,
          decoded,
          message:
            status === VinAddStatus.PENDING
              ? 'Request is being processed.'
              : 'Vehicle successfully added.',
        });
      }),
    );
  }

  getRequestStatus(requestId: string): Observable<ApiEnvelope<VinRequestStatusData>> {
    const exceptionEligibleFixtures: Record<string, string> = {
      'lsvp78-window-expired': 'WINDOW_EXPIRED',
      'lsvp78-class-not-found': 'CLASS_NOT_FOUND',
      'lsvp78-same-screen': 'WINDOW_EXPIRED',
    };

    return of(null).pipe(
      delay(this.randomDelay(200, 500)),
      map(() => {
        const fixtureReason = exceptionEligibleFixtures[requestId];
        if (fixtureReason) {
          return this.successResponse<VinRequestStatusData>({
            requestId,
            status: VinAddStatus.FAILED_INELIGIBLE,
            vin: '1HGCM82633A123456',
            lastUpdatedAt: new Date().toISOString(),
            eligibilityAllowed: false,
            eligibilityReasonCode: fixtureReason,
          });
        }

        const request = this._requests().get(requestId);
        if (!request) {
          return this.errorResponse<VinRequestStatusData>(
            ApiErrorCodes.CONTRACT_NOT_FOUND,
            'Request not found.',
          );
        }

        return this.successResponse<VinRequestStatusData>({
          requestId: request.id,
          status: request.status,
          vin: request.vin,
          decoded: request.decoded,
          lastUpdatedAt: request.lastUpdatedAt.toISOString(),
          eligibilityAllowed: request.status !== VinAddStatus.FAILED_INELIGIBLE,
          eligibilityReasonCode: request.eligibilityReasonCode ?? 'OK',
        });
      }),
    );
  }

  // ============== Admin Auth ==============

  devLogin(email: string, _password: string): Observable<ApiEnvelope<AdminLoginResponseData>> {
    return of(null).pipe(
      delay(this.randomDelay(200, 500)),
      map(() => {
        const role = email.toLowerCase().includes('support') ? 'support' : 'admin';
        const isAdmin = role === 'admin';
        const token = `mock-admin-portal-${isAdmin ? 'admin' : 'support'}-${Date.now()}`;

        return this.successResponse<AdminLoginResponseData>({
          token,
          expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
          email,
          role,
          displayName: isAdmin ? 'Dev Admin' : 'Dev Support',
        });
      }),
    );
  }

  // ============== Admin Operations ==============

  searchContracts(
    params: {
      search?: string;
      page?: number;
      limit?: number;
      sort?: 'createdAt' | 'externalContractId' | 'status';
      order?: 'ASC' | 'DESC';
    } = {},
  ): Observable<ApiEnvelope<AdminContractSearchData>> {
    return of(null).pipe(
      delay(this.randomDelay(300, 800)),
      map(() => {
        const term = params.search?.trim() ?? '';
        const page = params.page ?? 1;
        const limit = params.limit ?? 25;
        const sort = params.sort ?? 'createdAt';
        const order = params.order ?? 'DESC';

        let rows = Array.from(this._requests().values()).map((r) => {
          const contract = this._contracts().get(r.contractContextId);
          const nameFromRequest = [r.customerFirstName, r.customerLastName]
            .filter((part): part is string => Boolean(part?.trim()))
            .join(' ')
            .trim();
          return {
            requestId: r.id,
            contractContextId: r.contractContextId,
            externalContractId: contract?.externalContractId ?? '',
            status: r.status,
            createdAt: r.createdAt.toISOString(),
            primaryVin: contract?.primaryVin ?? null,
            primaryVehicle: formatMockVehicleLabel(
              contract?.primaryYear,
              contract?.primaryMake,
              contract?.primaryModel,
            ),
            secondaryVin: r.vin || null,
            secondaryVehicle: formatMockVehicleLabel(
              r.decoded?.year,
              r.decoded?.make,
              r.decoded?.model,
            ),
            customerName: nameFromRequest || contract?.customerName || null,
            customerEmail: r.customerEmail ?? null,
            committedVinMasked: contract?.additionalVinMasked,
            committedAt: contract?.additionalVinCommittedAt ?? null,
            exceptionId: contract?.exceptionId,
            contractNumber: contract?.contractNumber ?? '',
          };
        });

        if (term) {
          rows = rows.filter(
            (row) =>
              row.contractNumber.includes(term) ||
              row.externalContractId === term ||
              row.requestId === term,
          );
        }

        rows.sort((a, b) => {
          const cmp =
            sort === 'createdAt'
              ? a.createdAt.localeCompare(b.createdAt)
              : sort === 'externalContractId'
                ? a.externalContractId.localeCompare(b.externalContractId)
                : String(a.status).localeCompare(String(b.status));
          return order === 'ASC' ? cmp : -cmp;
        });

        const total = rows.length;
        const start = (page - 1) * limit;
        const results = rows
          .slice(start, start + limit)
          .map(({ contractNumber: _contractNumber, ...rest }) => rest);

        return this.successResponse<AdminContractSearchData>({
          results,
          page,
          limit,
          total,
          sort,
          order,
        });
      }),
    );
  }

  getContractDetail(contractContextId: string): Observable<ApiEnvelope<AdminContractDetailData>> {
    return of(null).pipe(
      delay(this.randomDelay(300, 800)),
      map(() => {
        const contract = this._contracts().get(contractContextId);
        if (!contract) {
          return this.errorResponse<AdminContractDetailData>(
            ApiErrorCodes.CONTRACT_NOT_FOUND,
            'Contract not found.',
          );
        }

        const requests = Array.from(this._requests().values())
          .filter((r) => r.contractContextId === contractContextId)
          .map((r) => ({
            requestId: r.id,
            status: r.status,
            createdAt: r.createdAt.toISOString(),
          }));

        return this.successResponse<AdminContractDetailData>({
          contractContextId: contract.id,
          externalContractId: contract.externalContractId,
          status: contract.hasAdditionalVin ? VinAddStatus.COMMITTED_LOCKED : VinAddStatus.NOT_USED,
          committedVinMasked: contract.additionalVinMasked ?? null,
          committedAt: contract.additionalVinCommittedAt ?? null,
          requests,
        });
      }),
    );
  }

  getRequestDetail(requestId: string): Observable<ApiEnvelope<AdminRequestDetailData>> {
    return of(null).pipe(
      delay(this.randomDelay(300, 800)),
      map(() => {
        const request = this._requests().get(requestId);
        if (!request) {
          return this.errorResponse<AdminRequestDetailData>(
            ApiErrorCodes.CONTRACT_NOT_FOUND,
            'Request not found.',
          );
        }

        return this.successResponse<AdminRequestDetailData>({
          requestId: request.id,
          contractContextId: request.contractContextId,
          status: request.status,
          vin: request.vin,
          decoded: request.decoded,
          eligibilityAllowed: true,
          eligibilityReasonCode: 'OK',
          emailStatus: null,
          confirmationDocument: {
            status: 'available',
            documentAvailable: true,
            canGenerate: true,
            canDownload: true,
            emailStatus: null,
            message: '',
            requestId: request.id,
          },
          audit: [
            {
              eventType: 'VIN_COMMIT_REQUESTED',
              createdAt: request.createdAt.toISOString(),
              actorType: 'CONSUMER',
            },
            {
              eventType:
                request.status === VinAddStatus.COMMITTED_LOCKED
                  ? 'VIN_COMMIT_SUCCESS'
                  : 'VIN_COMMIT_PENDING',
              createdAt: request.lastUpdatedAt.toISOString(),
              actorType: 'SYSTEM',
            },
          ],
        });
      }),
    );
  }

  addNote(requestId: string, _note: string): Observable<ApiEnvelope<AdminNoteResponseData>> {
    return of(null).pipe(
      delay(this.randomDelay(200, 500)),
      map(() => {
        const request = this._requests().get(requestId);
        if (!request) {
          return this.errorResponse<AdminNoteResponseData>(
            ApiErrorCodes.CONTRACT_NOT_FOUND,
            'Request not found.',
          );
        }

        // In a real app, we'd store the note
        return this.successResponse<AdminNoteResponseData>({
          requestId,
          noteSaved: true,
        });
      }),
    );
  }

  // ============== Document Operations ==============

  generateDocument(requestId: string): Observable<Blob> {
    return of(null).pipe(
      delay(this.randomDelay(300, 800)),
      map(() => {
        // Generate a minimal valid PDF
        const pdfContent = [
          '%PDF-1.4',
          '1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj',
          '2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj',
          '3 0 obj<</Type/Page/MediaBox[0 0 612 792]/Parent 2 0 R/Contents 4 0 R/Resources<</Font<</F1 5 0 R>>>>>>endobj',
          '4 0 obj<</Length 136>>stream',
          'BT /F1 24 Tf 100 700 Td (VIN Portal - Confirmation) Tj ET',
          `BT /F1 12 Tf 100 660 Td (Reference: ${requestId}) Tj ET`,
          `BT /F1 12 Tf 100 640 Td (Date: ${new Date().toISOString()}) Tj ET`,
          'BT /F1 12 Tf 100 600 Td (This is a confirmation stub document.) Tj ET',
          'endstream endobj',
          '5 0 obj<</Type/Font/Subtype/Type1/BaseFont/Helvetica>>endobj',
          'xref',
          'trailer<</Size 6/Root 1 0 R>>',
          '%%EOF',
        ].join('\n');

        return new Blob([pdfContent], { type: 'application/pdf' });
      }),
    );
  }

  emailDocument(
    requestId: string,
    _email: string,
  ): Observable<ApiEnvelope<{ requestId: string; sent: boolean }>> {
    return of(null).pipe(
      delay(this.randomDelay(500, 1200)),
      map(() => {
        return this.successResponse({ requestId, sent: true });
      }),
    );
  }

  // ============== Admin Exception Dashboard (ops-dashboard-001) ==============

  getExceptionDashboard(params: {
    range: string;
    store: string;
    from?: string;
    to?: string;
  }): Observable<ApiEnvelope<import('@core/models').ExceptionDashboardData>> {
    return of(null).pipe(
      delay(this.randomDelay(300, 700)),
      map(() => this.successResponse(buildMockDashboard(params))),
    );
  }

  getExceptionQueue(params: {
    status: string;
    search: string;
    range: string;
    store: string;
    from?: string;
    to?: string;
  }): Observable<ApiEnvelope<import('@core/models').ExceptionQueueData>> {
    return of(null).pipe(
      delay(this.randomDelay(300, 700)),
      map(() => this.successResponse(buildMockQueue(params))),
    );
  }

  getExceptionDetail(
    id: string,
  ): Observable<ApiEnvelope<import('@core/models').ExceptionDetailData>> {
    return of(null).pipe(
      delay(this.randomDelay(200, 500)),
      map(() => this.successResponse(buildMockDetail(id))),
    );
  }

  approveException(
    id: string,
  ): Observable<ApiEnvelope<import('@core/models').ApproveExceptionData>> {
    return of(null).pipe(
      delay(this.randomDelay(200, 500)),
      map(() => this.successResponse({ id, status: 'APPROVED' as const })),
    );
  }

  denyException(
    id: string,
    failureReason: string,
  ): Observable<ApiEnvelope<import('@core/models').DenyExceptionData>> {
    return of(null).pipe(
      delay(this.randomDelay(200, 500)),
      map(() => this.successResponse({ id, status: 'DENIED' as const, failureReason })),
    );
  }

  getDenialReasonsReport(
    params: { range: string; store: string; from?: string; to?: string },
    generatedBy: string,
  ): Observable<ApiEnvelope<import('@core/models').DenialReasonsReportData>> {
    return of(null).pipe(
      delay(this.randomDelay(300, 700)),
      map(() => this.successResponse(buildMockDenialReport(params, generatedBy))),
    );
  }

  // ============== Helpers ==============

  private initializeMockData(): void {
    const contracts: MockContract[] = [
      {
        id: 'ctx-1234567',
        contractNumber: '1234567',
        externalContractId: 'EXT-001',
        primaryVin: '1HGCM82633A123456',
        primaryVinMasked: '1HG******1234',
        primaryYear: '2019',
        primaryMake: 'Honda',
        primaryModel: 'Accord',
        customerName: 'Jane Smith',
        hasAdditionalVin: false,
      },
      {
        id: 'ctx-7654321',
        contractNumber: '7654321',
        externalContractId: 'EXT-002',
        primaryVin: '5FNYF6H07KB004811',
        primaryVinMasked: '5FN******5678',
        primaryYear: '2019',
        primaryMake: 'Honda',
        primaryModel: 'Pilot',
        customerName: 'Robert Jones',
        hasAdditionalVin: true,
        additionalVinMasked: '3N1******9012',
        additionalVinCommittedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'ctx-A234567',
        contractNumber: 'A234567',
        externalContractId: 'EXT-003',
        primaryVin: 'WVWZZZ3CZWE000133',
        primaryVinMasked: 'WVW******2345',
        primaryYear: '2020',
        primaryMake: 'Volkswagen',
        primaryModel: 'Golf',
        customerName: 'Maria Garcia',
        hasAdditionalVin: false,
        exceptionId: 'exc-10043',
      },
      {
        id: 'ctx-B345678',
        contractNumber: 'B345678',
        externalContractId: 'EXT-004',
        primaryVin: '3N1AB7AP5KY100007',
        primaryVinMasked: '3N1******6789',
        primaryYear: '2019',
        primaryMake: 'Nissan',
        primaryModel: 'Sentra',
        customerName: 'Chris Williams',
        hasAdditionalVin: false,
        exceptionId: 'exc-10041',
      },
      {
        id: 'ctx-D567890',
        contractNumber: 'D567890',
        externalContractId: 'EXT-006',
        primaryVin: 'JTDKARFU0K3071090',
        primaryVinMasked: 'JTD******7890',
        primaryYear: '2019',
        primaryMake: 'Toyota',
        primaryModel: 'Prius',
        customerName: 'Alex Martinez',
        hasAdditionalVin: false,
      },
      {
        id: 'ctx-E678901',
        contractNumber: 'E678901',
        externalContractId: 'EXT-007',
        primaryVin: '2HGFC2F59MH567890',
        primaryVinMasked: '2HG******4567',
        primaryYear: '2021',
        primaryMake: 'Honda',
        primaryModel: 'Civic',
        customerName: 'Taylor Brooks',
        hasAdditionalVin: true,
        additionalVinMasked: '1FT******8901',
        additionalVinCommittedAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
      },
    ];

    this._contracts.set(new Map(contracts.map((c) => [c.id, c])));

    // Pre-seed requests for contracts that already have committed VINs
    const requests: MockVinRequest[] = [
      {
        id: 'req-7654321-001',
        contractContextId: 'ctx-7654321',
        vin: '3N1AB7AP5KY000012',
        decoded: {
          year: 2023,
          make: 'Nissan',
          model: 'Sentra',
        },
        status: VinAddStatus.COMMITTED_LOCKED,
        idempotencyKey: this.generateMockIdempotencyKey('ctx-7654321', 'req-7654321-001'),
        customerFirstName: 'Robert',
        customerLastName: 'Jones',
        customerEmail: 'robert.jones@example.com',
        createdAt: new Date(Date.now() - 25 * 60 * 60 * 1000),
        lastUpdatedAt: new Date(Date.now() - 24 * 60 * 60 * 1000),
      },
      {
        id: 'req-E678901-001',
        contractContextId: 'ctx-E678901',
        vin: '1FTEW1EP5KFA00034',
        decoded: {
          year: 2022,
          make: 'Ford',
          model: 'F-150',
        },
        status: VinAddStatus.COMMITTED_LOCKED,
        idempotencyKey: this.generateMockIdempotencyKey('ctx-E678901', 'req-E678901-001'),
        customerFirstName: 'Taylor',
        customerLastName: 'Brooks',
        customerEmail: 'taylor.brooks@example.com',
        createdAt: new Date(Date.now() - 49 * 60 * 60 * 1000),
        lastUpdatedAt: new Date(Date.now() - 48 * 60 * 60 * 1000),
      },
    ];

    this._requests.set(new Map(requests.map((r) => [r.id, r])));
  }

  private getOrCreateContract(id: string, contractNumber: string): MockContract {
    const existing = this._contracts().get(id);
    if (existing) return existing;

    const newContract: MockContract = {
      id,
      contractNumber,
      externalContractId: `EXT-${contractNumber}`,
      primaryVinMasked: '1XX******' + Math.random().toString().slice(2, 6),
      hasAdditionalVin: false,
    };

    this._contracts.update((m) => {
      m.set(id, newContract);
      return new Map(m);
    });

    return newContract;
  }

  // ============== Admin User Management (ops-users-001) ==============

  getAdminUsers(): Observable<ApiEnvelope<AdminUserListData>> {
    return of(null).pipe(
      delay(this.randomDelay(200, 600)),
      map(() => this.successResponse<AdminUserListData>(this.buildAdminUserList())),
    );
  }

  createAdminUser(body: AdminUserCreateRequest): Observable<ApiEnvelope<AdminUserResponseData>> {
    return of(null).pipe(
      delay(this.randomDelay(200, 400)),
      map(() => {
        const org = body.email.endsWith('@lithia.com') ? 'Lithia' : 'SGI';
        const user: AdminUser = {
          id: this.generateId('usr'),
          name: body.name,
          email: body.email,
          role: body.role,
          org,
          scope: org === 'Lithia' ? 'All 13 stores' : 'All stores',
          mfaEnabled: false,
          status: body.status ?? 'invited',
          lastActiveAt: null,
        };
        MOCK_ADMIN_USERS.push(user);
        return this.successResponse({ user });
      }),
    );
  }

  updateAdminUser(
    userId: string,
    body: AdminUserUpdateRequest,
  ): Observable<ApiEnvelope<AdminUserResponseData>> {
    return of(null).pipe(
      delay(this.randomDelay(200, 400)),
      map(() => {
        const index = MOCK_ADMIN_USERS.findIndex((u) => u.id === userId);
        if (index === -1) {
          return this.errorResponse<AdminUserResponseData>('NOT_FOUND', 'User not found.');
        }
        const current = MOCK_ADMIN_USERS[index];
        const org = (body.email ?? current.email).endsWith('@lithia.com') ? 'Lithia' : 'SGI';
        const updated: AdminUser = {
          ...current,
          name: body.name ?? current.name,
          email: body.email ?? current.email,
          role: body.role ?? current.role,
          status: body.status ?? current.status,
          org,
          scope: org === 'Lithia' ? 'All 13 stores' : 'All stores',
        };
        MOCK_ADMIN_USERS[index] = updated;
        return this.successResponse({ user: updated });
      }),
    );
  }

  deleteAdminUser(userId: string): Observable<ApiEnvelope<AdminUserDeleteData>> {
    return of(null).pipe(
      delay(this.randomDelay(200, 400)),
      map(() => {
        const index = MOCK_ADMIN_USERS.findIndex((u) => u.id === userId);
        if (index === -1) {
          return this.errorResponse<AdminUserDeleteData>('NOT_FOUND', 'User not found.');
        }
        MOCK_ADMIN_USERS.splice(index, 1);
        return this.successResponse({ id: userId, deleted: true });
      }),
    );
  }

  // ============== Admin Audit Events / System Logs (ops-logs-001) ==============

  getAdminAuditEvents(params: {
    type: AdminAuditFilterKey;
    range: string;
    store: string;
    from?: string;
    to?: string;
  }): Observable<ApiEnvelope<AdminAuditEventListData>> {
    return of(null).pipe(
      delay(this.randomDelay(200, 600)),
      map(() => this.successResponse<AdminAuditEventListData>(buildMockAuditEvents(params))),
    );
  }

  private buildAdminUserList(): AdminUserListData {
    const users = MOCK_ADMIN_USERS.map((u) => ({ ...u }));
    const stats = [
      { label: 'Total Users', value: users.length },
      { label: 'Active', value: users.filter((u) => u.status === 'active').length },
      { label: 'Super Admins', value: users.filter((u) => u.role === 'super_admin').length },
      { label: 'Pending Invites', value: users.filter((u) => u.status === 'invited').length },
    ];
    return { users, stats };
  }

  private generateId(prefix: string): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  /** Opaque mock idempotency key so secret scanners do not flag seeded dev data. */
  private generateMockIdempotencyKey(contractContextId: string, requestId: string): string {
    return `mock-idem-${contractContextId}-${requestId}`;
  }

  /** Opaque dev token (not JWT-shaped) so secret scanners do not flag mock responses. */
  private generateMockSessionToken(contractContextId: string): string {
    return `vin-mock-session-${contractContextId}-${Date.now()}`;
  }

  private randomDelay(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  private successResponse<T>(data: T): ApiEnvelope<T> {
    return {
      correlationId: this.generateId('corr'),
      success: true,
      data,
      error: null,
    };
  }

  private errorResponse<T>(
    code: string,
    message: string,
    details?: Record<string, unknown>,
  ): ApiEnvelope<T> {
    return {
      correlationId: this.generateId('corr'),
      success: false,
      data: null,
      error: { code, message, details },
    };
  }
}

// ============== Admin Exception Dashboard Mock Data ==============

function rangeLabelFor(range: string, from?: string, to?: string): string {
  if (range === 'custom') return from && to ? `${from} – ${to}` : 'custom range';
  if (range === '7') return 'last 7 days';
  if (range === '90') return 'last 90 days';
  return 'last 30 days';
}

function rangeMultiplier(range: string): number {
  if (range === '7') return 0.4;
  if (range === '90') return 2.4;
  return 1;
}

function buildMockDashboard(params: {
  range: string;
  store: string;
  from?: string;
  to?: string;
}): import('@core/models').ExceptionDashboardData {
  const m = rangeMultiplier(params.range);
  const storeFactor = params.store === 'all' ? 1 : 0.18;

  const awaitingDecision = Math.max(1, Math.round(14 * storeFactor));
  const atRisk = Math.max(0, Math.round(5 * storeFactor));
  const approved = Math.round(132 * m * storeFactor);
  const denied = Math.round(38 * m * storeFactor);
  const decidedTotal = approved + denied;
  const approvalRatePct = decidedTotal === 0 ? 0 : Math.round((approved / decidedTotal) * 100);

  const now = Date.now();
  const HOUR = 60 * 60 * 1000;
  const queueSeed: ReadonlyArray<{
    caseRef: string;
    customerName: string;
    vin: string;
    store: string;
    failureReason: string;
    submittedHoursAgo: number;
    slaHoursRemaining: number;
  }> = [
    {
      caseRef: 'EXC-10043',
      customerName: 'Maria Lopez',
      vin: '1HGCM82633A004352',
      store: 'Lithia Toyota of Medford',
      failureReason: 'Eligibility window expired',
      submittedHoursAgo: 52,
      slaHoursRemaining: -4,
    },
    {
      caseRef: 'EXC-10041',
      customerName: 'Brian Chen',
      vin: '5FNYF6H07KB004811',
      store: 'Lithia Hyundai of Reno',
      failureReason: 'Vehicle class mismatch',
      submittedHoursAgo: 38,
      slaHoursRemaining: 6,
    },
    {
      caseRef: 'EXC-10039',
      customerName: 'Janet Patel',
      vin: 'WVWZZZ3CZWE000133',
      store: 'Lithia Ford of Boise',
      failureReason: 'Mileage over limit',
      submittedHoursAgo: 28,
      slaHoursRemaining: 20,
    },
    {
      caseRef: 'EXC-10037',
      customerName: 'Derrick Hall',
      vin: '3N1AB7AP5KY100007',
      store: 'Lithia CJDR of Bend',
      failureReason: 'Prior VIN on contract',
      submittedHoursAgo: 9,
      slaHoursRemaining: 39,
    },
    {
      caseRef: 'EXC-10036',
      customerName: 'Sofia Romero',
      vin: 'JTDKARFU0K3071090',
      store: 'Lithia Subaru of Fresno',
      failureReason: 'State restricted',
      submittedHoursAgo: 3,
      slaHoursRemaining: 45,
    },
  ];

  const slaUrgencyFor = (slaHoursRemaining: number) => {
    if (slaHoursRemaining < 0) return 'overdue' as const;
    if (slaHoursRemaining < 12) return 'due_12h' as const;
    if (slaHoursRemaining < 24) return 'due_24h' as const;
    return 'on_track' as const;
  };

  const allRows = queueSeed.map((row, i) => ({
    id: `exc-${10043 - i}`,
    caseRef: row.caseRef,
    customerName: row.customerName,
    vin: row.vin,
    store: row.store,
    submittedAt: new Date(now - row.submittedHoursAgo * HOUR).toISOString(),
    slaDueAt: new Date(now + row.slaHoursRemaining * HOUR).toISOString(),
    slaUrgency: slaUrgencyFor(row.slaHoursRemaining),
    failureReason: row.failureReason,
    vinAddRequestId: `req-${10043 - i}`,
  }));

  const pendingQueue =
    params.store === 'all'
      ? allRows.slice(0, awaitingDecision)
      : allRows.filter((r) => r.store === params.store).slice(0, awaitingDecision);

  const submittedLast24h = pendingQueue.filter(
    (r) => Date.now() - new Date(r.submittedAt).getTime() < 24 * HOUR,
  ).length;

  const slaAging = pendingQueue.reduce(
    (acc, row) => {
      if (row.slaUrgency === 'overdue') acc.pastSla += 1;
      else if (row.slaUrgency === 'due_12h') acc.due12h += 1;
      else if (row.slaUrgency === 'due_24h') acc.due24h += 1;
      else acc.onTrack += 1;
      return acc;
    },
    { pastSla: 0, due12h: 0, due24h: 0, onTrack: 0 },
  );

  const urgencyRank: Record<string, number> = {
    overdue: 0,
    due_12h: 1,
    due_24h: 2,
    on_track: 3,
  };
  const sortedQueue = [...pendingQueue].sort(
    (a, b) =>
      urgencyRank[a.slaUrgency] - urgencyRank[b.slaUrgency] ||
      new Date(a.slaDueAt).getTime() - new Date(b.slaDueAt).getTime(),
  );

  const bucketCount = params.range === '7' ? 7 : params.range === '90' ? 12 : 30;
  const dayMs = 24 * HOUR;
  const totalApproved = approved;
  const totalDenied = denied;
  const totalSubmitted = totalApproved + totalDenied + Math.round(awaitingDecision * 0.6);
  const volumeTrend = Array.from({ length: bucketCount }, (_, i) => {
    const bucketDate = new Date(now - (bucketCount - 1 - i) * dayMs);
    const dateIso = bucketDate.toISOString().slice(0, 10);
    const phase = (i + 1) / bucketCount;
    const wave = 0.6 + 0.4 * Math.sin(i * 1.1);
    return {
      date: dateIso,
      submitted: Math.max(0, Math.round((totalSubmitted / bucketCount) * (wave + phase * 0.3))),
      approved: Math.max(0, Math.round((totalApproved / bucketCount) * (wave + phase * 0.2))),
      denied: Math.max(0, Math.round((totalDenied / bucketCount) * (wave + phase * 0.1))),
    };
  });

  const denialSeed: ReadonlyArray<{ reason: string; weight: number }> = [
    { reason: 'Eligibility window expired', weight: 0.32 },
    { reason: 'Vehicle class mismatch', weight: 0.24 },
    { reason: 'Mileage over limit', weight: 0.18 },
    { reason: 'Prior VIN on contract', weight: 0.14 },
    { reason: 'State restricted', weight: 0.08 },
    { reason: 'Other', weight: 0.04 },
  ];
  const denialReasons = denialSeed
    .map((seed) => {
      const count = Math.max(0, Math.round(totalDenied * seed.weight));
      return { reason: seed.reason, count };
    })
    .filter((r) => r.count > 0)
    .map((r) => ({
      ...r,
      pct: totalDenied === 0 ? 0 : Math.round((r.count / totalDenied) * 100),
    }));

  return {
    rangeLabel: rangeLabelFor(params.range, params.from, params.to),
    decidedTotal,
    pendingTotal: sortedQueue.length,
    kpis: {
      awaitingDecision: sortedQueue.length,
      submittedLast24h,
      atRisk,
      approved,
      denied,
      avgDecisionHours: Math.round((14 - m) * 10) / 10,
    },
    slaAging,
    outcomes: { approvalRatePct, approved, denied },
    pendingQueue: sortedQueue,
    volumeTrend,
    denialReasons,
  };
}

function buildMockQueue(params: {
  status: string;
  search: string;
  range: string;
  store: string;
  from?: string;
  to?: string;
}): import('@core/models').ExceptionQueueData {
  const HOUR = 60 * 60 * 1000;
  const now = Date.now();

  type Row = import('@core/models').ExceptionQueueRow;
  const seed: ReadonlyArray<{
    id: string;
    caseRef: string;
    customerName: string;
    vin: string;
    vehicleLabel: string;
    store: string;
    failureReason: string;
    submittedHoursAgo: number;
    slaHoursRemaining: number;
    status: Row['status'];
  }> = [
    {
      id: 'exc-10043',
      caseRef: 'EXC-10043',
      customerName: 'Maria Lopez',
      vin: '1HGCM82633A004352',
      vehicleLabel: '2024 Honda Pilot Elite',
      store: 'Lithia Toyota of Medford',
      failureReason: 'Eligibility window expired',
      submittedHoursAgo: 52,
      slaHoursRemaining: -4,
      status: 'PENDING',
    },
    {
      id: 'exc-10041',
      caseRef: 'EXC-10041',
      customerName: 'Brian Chen',
      vin: '5FNYF6H07KB004811',
      vehicleLabel: '2023 Honda Pilot Touring',
      store: 'Lithia Hyundai of Reno',
      failureReason: 'Vehicle class mismatch',
      submittedHoursAgo: 38,
      slaHoursRemaining: 6,
      status: 'PENDING',
    },
    {
      id: 'exc-10039',
      caseRef: 'EXC-10039',
      customerName: 'Janet Patel',
      vin: 'WVWZZZ3CZWE000133',
      vehicleLabel: '2022 Volkswagen Atlas',
      store: 'Lithia Ford of Boise',
      failureReason: 'Mileage over limit',
      submittedHoursAgo: 28,
      slaHoursRemaining: 20,
      status: 'PENDING',
    },
    {
      id: 'exc-10037',
      caseRef: 'EXC-10037',
      customerName: 'Derrick Hall',
      vin: '3N1AB7AP5KY100007',
      vehicleLabel: '2021 Nissan Sentra',
      store: 'Lithia CJDR of Bend',
      failureReason: 'Prior VIN on contract',
      submittedHoursAgo: 9,
      slaHoursRemaining: 39,
      status: 'PENDING',
    },
    {
      id: 'exc-10036',
      caseRef: 'EXC-10036',
      customerName: 'Sofia Romero',
      vin: 'JTDKARFU0K3071090',
      vehicleLabel: '2023 Toyota Prius',
      store: 'Lithia Subaru of Fresno',
      failureReason: 'State restricted',
      submittedHoursAgo: 3,
      slaHoursRemaining: 45,
      status: 'PENDING',
    },
    {
      id: 'exc-10030',
      caseRef: 'EXC-10030',
      customerName: 'Hannah Walker',
      vin: '2C4RDGCG4KR100021',
      vehicleLabel: '2022 Dodge Grand Caravan',
      store: 'Lithia Toyota of Medford',
      failureReason: 'Eligibility window expired',
      submittedHoursAgo: 80,
      slaHoursRemaining: 0,
      status: 'APPROVED',
    },
    {
      id: 'exc-10028',
      caseRef: 'EXC-10028',
      customerName: 'Carlos Mendes',
      vin: '1FTEW1EP5KFA00034',
      vehicleLabel: '2022 Ford F-150 Lariat',
      store: 'Lithia Hyundai of Reno',
      failureReason: 'Vehicle class mismatch',
      submittedHoursAgo: 110,
      slaHoursRemaining: 0,
      status: 'DENIED',
    },
    {
      id: 'exc-10024',
      caseRef: 'EXC-10024',
      customerName: 'Priya Singh',
      vin: '5YJ3E1EA7KF000089',
      vehicleLabel: '2024 Tesla Model 3',
      store: 'Lithia Kia of Anchorage',
      failureReason: 'Other',
      submittedHoursAgo: 140,
      slaHoursRemaining: 0,
      status: 'APPROVED',
    },
  ];

  const slaUrgencyFor = (hours: number): import('@core/models').SlaUrgency => {
    if (hours < 0) return 'overdue';
    if (hours < 12) return 'due_12h';
    if (hours < 24) return 'due_24h';
    return 'on_track';
  };

  const allRows: Row[] = seed.map((s) => ({
    id: s.id,
    caseRef: s.caseRef,
    customerName: s.customerName,
    vin: s.vin,
    vehicleLabel: s.vehicleLabel,
    store: s.store,
    submittedAt: new Date(now - s.submittedHoursAgo * HOUR).toISOString(),
    slaDueAt: new Date(now + s.slaHoursRemaining * HOUR).toISOString(),
    slaUrgency: slaUrgencyFor(s.slaHoursRemaining),
    failureReason: s.failureReason,
    status: s.status,
    vinAddRequestId: `req-${s.id.replace('exc-', '')}`,
  }));

  const scoped = allRows.filter((r) => params.store === 'all' || r.store === params.store);

  const statusCounts = {
    all: scoped.length,
    PENDING: scoped.filter((r) => r.status === 'PENDING').length,
    APPROVED: scoped.filter((r) => r.status === 'APPROVED').length,
    DENIED: scoped.filter((r) => r.status === 'DENIED').length,
  };

  let rows = params.status === 'all' ? scoped : scoped.filter((r) => r.status === params.status);

  const q = params.search.trim().toLowerCase();
  if (q) {
    rows = rows.filter(
      (r) =>
        r.caseRef.toLowerCase().includes(q) ||
        r.customerName.toLowerCase().includes(q) ||
        r.vin.toLowerCase().includes(q) ||
        r.failureReason.toLowerCase().includes(q),
    );
  }

  const statusRank: Record<Row['status'], number> = { PENDING: 0, APPROVED: 1, DENIED: 2 };
  const urgencyRank: Record<string, number> = { overdue: 0, due_12h: 1, due_24h: 2, on_track: 3 };
  rows = [...rows].sort((a, b) => {
    const s = statusRank[a.status] - statusRank[b.status];
    if (s !== 0) return s;
    if (a.status === 'PENDING') {
      return (
        urgencyRank[a.slaUrgency] - urgencyRank[b.slaUrgency] ||
        new Date(a.slaDueAt).getTime() - new Date(b.slaDueAt).getTime()
      );
    }
    return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime();
  });

  return {
    rangeLabel: rangeLabelFor(params.range, params.from, params.to),
    rows,
    totalCount: rows.length,
    statusCounts,
  };
}

// ============== Admin Exception Detail Mock Data (ops-drawer-001) ==============

interface DetailSeed {
  caseRef: string;
  customerName: string;
  customerEmail?: string;
  customerZip: string;
  primaryContractRef: string;
  primaryVin: string;
  primaryLabel: string;
  primaryClass: string | null;
  secondaryVin: string;
  secondaryLabel: string;
  secondaryClass: string | null;
  reasonCode: string;
  reasonLabel: string;
  reasonDetail: string;
  source: 'CONSUMER' | 'DMS';
  status: 'PENDING' | 'APPROVED' | 'DENIED';
  submittedHoursAgo: number;
  approverName?: string;
  failureReason?: string;
}

const detailSeeds: Record<string, DetailSeed> = {
  'exc-10043': {
    caseRef: 'EXC-10043',
    customerName: 'Maria Lopez',
    customerEmail: 'maria.lopez@example.com',
    customerZip: '97501',
    primaryContractRef: 'LOC-882144',
    primaryVin: '1HGCM82633A004001',
    primaryLabel: '2019 Honda Pilot EX-L',
    primaryClass: 'Mid-size SUV · Tier 2',
    secondaryVin: '1HGCM82633A004352',
    secondaryLabel: '2024 Honda Pilot Elite',
    secondaryClass: 'Full-size SUV · Tier 3',
    reasonCode: 'Eligibility window expired',
    reasonLabel: 'Eligibility window expired',
    reasonDetail:
      'The 90-day eligibility window for adding a secondary VIN to this contract closed before the request was submitted. The dataset shows last eligible day was 12 days prior to submission.',
    source: 'CONSUMER',
    status: 'PENDING',
    submittedHoursAgo: 52,
  },
  'exc-10041': {
    caseRef: 'EXC-10041',
    customerName: 'Brian Chen',
    customerZip: '89502',
    primaryContractRef: 'LOC-882102',
    primaryVin: '5FNYF6H07KB004100',
    primaryLabel: '2020 Honda Pilot EX',
    primaryClass: 'Mid-size SUV · Tier 2',
    secondaryVin: '5FNYF6H07KB004811',
    secondaryLabel: '2023 Honda Pilot Touring',
    secondaryClass: 'Full-size SUV · Tier 3',
    reasonCode: 'Vehicle class mismatch',
    reasonLabel: 'Vehicle class mismatch',
    reasonDetail:
      'Secondary VIN classes one tier above the primary contract vehicle in the Lithia classing dataset. Manual review required to confirm whether the customer has an upgrade rider on file.',
    source: 'DMS',
    status: 'PENDING',
    submittedHoursAgo: 38,
  },
  'exc-10030': {
    caseRef: 'EXC-10030',
    customerName: 'Hannah Walker',
    customerZip: '97501',
    primaryContractRef: 'LOC-879014',
    primaryVin: '2C4RDGCG4KR099100',
    primaryLabel: '2020 Dodge Grand Caravan',
    primaryClass: 'Minivan · Tier 2',
    secondaryVin: '2C4RDGCG4KR100021',
    secondaryLabel: '2022 Dodge Grand Caravan',
    secondaryClass: 'Minivan · Tier 2',
    reasonCode: 'Eligibility window expired',
    reasonLabel: 'Eligibility window expired',
    reasonDetail:
      'Eligibility window expired by 4 days. Customer provided written purchase order pre-dating expiry; admin approved on review.',
    source: 'CONSUMER',
    status: 'APPROVED',
    submittedHoursAgo: 80,
    approverName: 'Avery Brooks',
  },
  'exc-10028': {
    caseRef: 'EXC-10028',
    customerName: 'Carlos Mendes',
    customerZip: '89502',
    primaryContractRef: 'LOC-878012',
    primaryVin: '1FTEW1EP5KFA00010',
    primaryLabel: '2020 Ford F-150 XLT',
    primaryClass: 'Light truck · Tier 2',
    secondaryVin: '1FTEW1EP5KFA00034',
    secondaryLabel: '2022 Ford F-150 Lariat',
    secondaryClass: 'Light truck · Tier 3',
    reasonCode: 'Vehicle class mismatch',
    reasonLabel: 'Vehicle class mismatch',
    reasonDetail:
      'Secondary VIN exceeds the contracted tier. Denied per policy SG-CL-4. Customer notified of remediation options.',
    source: 'CONSUMER',
    status: 'DENIED',
    submittedHoursAgo: 110,
    approverName: 'Jordan Lee',
    failureReason: 'Vehicle class mismatch',
  },
};

function classBadgeFor(tier: string | null): 'ok' | 'warn' | 'unknown' {
  if (!tier) return 'unknown';
  if (tier.toLowerCase().includes('tier 3')) return 'warn';
  return 'ok';
}

function buildMockDetail(id: string): import('@core/models').ExceptionDetailData {
  const seed: DetailSeed = detailSeeds[id] ??
    detailSeeds['exc-10043'] ?? {
      caseRef: id.toUpperCase(),
      customerName: 'Unknown Customer',
      customerZip: '00000',
      primaryContractRef: 'LOC-000000',
      primaryVin: '00000000000000000',
      primaryLabel: 'Primary vehicle',
      primaryClass: null,
      secondaryVin: '00000000000000000',
      secondaryLabel: 'Secondary vehicle',
      secondaryClass: null,
      reasonCode: 'Other',
      reasonLabel: 'Other',
      reasonDetail: 'Detail not available for this case.',
      source: 'CONSUMER',
      status: 'PENDING',
      submittedHoursAgo: 24,
    };

  const HOUR = 60 * 60 * 1000;
  const submittedAt = new Date(Date.now() - seed.submittedHoursAgo * HOUR);
  const submittedIso = submittedAt.toISOString();
  const decidedIso = new Date(submittedAt.getTime() + 6 * HOUR).toISOString();
  const sourceLabel =
    seed.source === 'DMS' ? 'Lithia DMS staff on behalf' : 'Customer self-service';

  const baseTimeline: import('@core/models').ExceptionTimelineStep[] = [
    {
      step: 'CONTRACT_AUTH',
      label: 'Contract authenticated',
      actor: seed.source === 'DMS' ? 'DMS STAFF' : 'CONSUMER',
      occurredAt: new Date(submittedAt.getTime() - 30 * 60 * 1000).toISOString(),
    },
    {
      step: 'VIN_DECODED',
      label: 'Secondary VIN decoded',
      actor: 'SYSTEM',
      occurredAt: new Date(submittedAt.getTime() - 25 * 60 * 1000).toISOString(),
    },
    {
      step: 'ELIGIBILITY_FAILED',
      label: 'Eligibility check failed',
      actor: 'SYSTEM',
      occurredAt: new Date(submittedAt.getTime() - 20 * 60 * 1000).toISOString(),
    },
    {
      step: 'EXCEPTION_SUBMITTED',
      label: 'Exception submitted',
      actor: seed.source === 'DMS' ? 'DMS STAFF' : 'CONSUMER',
      occurredAt: submittedIso,
    },
    {
      step: 'SF_CASE_CREATED',
      label: 'Salesforce case created',
      actor: 'SYSTEM',
      occurredAt: new Date(submittedAt.getTime() + 2 * 60 * 1000).toISOString(),
    },
    {
      step: 'LITHIA_NOTIFIED',
      label: 'Lithia DMS notified',
      actor: 'SYSTEM',
      occurredAt: new Date(submittedAt.getTime() + 5 * 60 * 1000).toISOString(),
    },
  ];

  if (seed.status !== 'PENDING') {
    baseTimeline.push(
      {
        step: 'DECISION_RECORDED',
        label: seed.status === 'APPROVED' ? 'Decision: approved' : 'Decision: denied',
        actor: 'ADMIN',
        occurredAt: decidedIso,
      },
      ...(seed.status === 'APPROVED'
        ? [
            {
              step: 'VIN_COMMITTED',
              label: 'Secondary VIN committed to contract',
              actor: 'SYSTEM' as const,
              occurredAt: new Date(new Date(decidedIso).getTime() + 60_000).toISOString(),
            },
          ]
        : []),
      {
        step: 'CUSTOMER_EMAILED',
        label: 'Customer email sent',
        actor: 'SYSTEM',
        occurredAt: new Date(new Date(decidedIso).getTime() + 120_000).toISOString(),
      },
    );
  }

  baseTimeline.sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());

  return {
    id,
    caseRef: seed.caseRef,
    salesforceCaseId: `500Hs00000${seed.caseRef.replace(/[^0-9]/g, '').padStart(6, '0')}`,
    customerName: seed.customerName,
    customerEmail: seed.customerEmail ?? null,
    customerZip: seed.customerZip,
    primaryContractRef: seed.primaryContractRef,
    submittedAt: submittedIso,
    submittedByLabel: sourceLabel,
    source: seed.source,
    reasonCode: seed.reasonCode,
    reasonLabel: seed.reasonLabel,
    reasonDetail: seed.reasonDetail,
    status: seed.status,
    classing: {
      rows: [
        {
          role: 'primary',
          vin: seed.primaryVin,
          vehicleLabel: seed.primaryLabel,
          classTier: seed.primaryClass,
          classBadgeVariant: classBadgeFor(seed.primaryClass),
        },
        {
          role: 'secondary',
          vin: seed.secondaryVin,
          vehicleLabel: seed.secondaryLabel,
          classTier: seed.secondaryClass,
          classBadgeVariant: classBadgeFor(seed.secondaryClass),
        },
      ],
      verdict:
        seed.primaryClass && seed.secondaryClass
          ? seed.primaryClass === seed.secondaryClass
            ? 'Classes match — within contracted tier'
            : 'Class tier comparison: secondary exceeds primary'
          : 'Vehicle not present in classing dataset',
    },
    timeline: baseTimeline,
    decision:
      seed.status === 'PENDING'
        ? undefined
        : {
            outcome: seed.status,
            approverName: seed.approverName ?? 'Avery Brooks',
            decidedAt: decidedIso,
            failureReason: seed.failureReason,
          },
    developer: {
      reasonCode: seed.reasonCode,
      dependency: seed.status === 'PENDING' ? 'awaiting decision' : 'OK',
      sourceIp: '10.42.12.78',
      correlationId: `corr-${seed.caseRef.toLowerCase()}-mock`,
      userAgent:
        'Mozilla/5.0 (Macintosh; Intel Mac OS X 14_4) AppleWebKit/605.1.15 Lithia/2.4 Safari/605.1',
    },
    vinAddRequestId: `req-${id}`,
    confirmationDocument: {
      status: 'available',
      documentAvailable: true,
      canGenerate: true,
      canDownload: true,
      emailStatus: seed.status === 'APPROVED' ? 'sent' : null,
      message: '',
      requestId: `req-${id}`,
    },
  };
}

// ============== Denial Reasons Report Mock (ops-reports-001) ==============

function buildMockDenialReport(
  params: { range: string; store: string; from?: string; to?: string },
  generatedBy: string,
): import('@core/models').DenialReasonsReportData {
  const dashboard = buildMockDashboard(params);
  const queue = buildMockQueue({
    status: 'all',
    search: '',
    range: params.range,
    store: params.store,
    from: params.from,
    to: params.to,
  });

  const totalRequests = dashboard.decidedTotal + dashboard.kpis.awaitingDecision;
  const approved = dashboard.outcomes.approved;
  const denied = dashboard.outcomes.denied;
  const approvalRatePct = dashboard.outcomes.approvalRatePct;

  const breakdown = dashboard.denialReasons.map((r) => ({
    reason: r.reason,
    count: r.count,
    sharePct: r.pct,
  }));

  const cases: import('@core/models').DenialReasonReportCaseRow[] = queue.rows.map((row) => ({
    caseRef: row.caseRef,
    submittedAt: row.submittedAt,
    store: row.store,
    customerName: row.customerName,
    contractRef: `LOC-${row.id.replace(/[^0-9]/g, '').padStart(6, '0')}`,
    secondaryVin: row.vin,
    vehicleLabel: row.vehicleLabel,
    source: row.id.endsWith('1') ? 'DMS' : 'CONSUMER',
    failureReason: row.failureReason,
    outcome: row.status,
    denialReason: row.status === 'DENIED' ? row.failureReason : '',
  }));

  return {
    rangeLabel: dashboard.rangeLabel,
    generatedAt: new Date().toISOString(),
    generatedBy,
    summary: { totalRequests, approved, denied, approvalRatePct },
    breakdown,
    cases,
  };
}

// ============== Mock Types ==============

interface MockContract {
  id: string;
  contractNumber: string;
  externalContractId: string;
  primaryVin?: string;
  primaryVinMasked: string;
  primaryYear?: string;
  primaryMake?: string;
  primaryModel?: string;
  customerName?: string;
  hasAdditionalVin: boolean;
  additionalVinMasked?: string;
  additionalVinCommittedAt?: string;
  exceptionId?: string;
}

interface MockVinRequest {
  id: string;
  contractContextId: string;
  vin: string;
  decoded: VinDecoded;
  status: VinAddStatus;
  eligibilityReasonCode?: string;
  idempotencyKey: string;
  customerFirstName?: string;
  customerLastName?: string;
  customerEmail?: string;
  createdAt: Date;
  lastUpdatedAt: Date;
}

interface MockOtpChallenge {
  id: string;
  contractContextId: string;
  code: string;
  expiresAt: Date;
  attempts: number;
}

// ============== Admin User Management mock data (ops-users-001) ==============

const MOCK_ADMIN_USERS: AdminUserListData['users'] = [
  {
    id: 'usr-001',
    name: 'Dara Okafor',
    email: 'dokafor@safeguardproducts.com',
    role: 'super_admin',
    org: 'SGI',
    scope: 'All stores',
    mfaEnabled: true,
    status: 'active',
    lastActiveAt: 'just now',
  },
  {
    id: 'usr-002',
    name: 'Luis Milani',
    email: 'lmilani@safeguardproducts.com',
    role: 'super_admin',
    org: 'SGI',
    scope: 'All stores',
    mfaEnabled: true,
    status: 'active',
    lastActiveAt: '2h ago',
  },
  {
    id: 'usr-003',
    name: 'Priya Raman',
    email: 'praman@safeguardproducts.com',
    role: 'operations_admin',
    org: 'SGI',
    scope: 'All stores',
    mfaEnabled: true,
    status: 'active',
    lastActiveAt: '38m ago',
  },
  {
    id: 'usr-004',
    name: 'Wesley Cho',
    email: 'wcho@safeguardproducts.com',
    role: 'operations_admin',
    org: 'SGI',
    scope: 'All stores',
    mfaEnabled: true,
    status: 'active',
    lastActiveAt: '4h ago',
  },
  {
    id: 'usr-005',
    name: 'Renee Foster',
    email: 'rfoster@safeguardproducts.com',
    role: 'operations_admin',
    org: 'SGI',
    scope: 'All stores',
    mfaEnabled: false,
    status: 'active',
    lastActiveAt: '1d ago',
  },
  {
    id: 'usr-006',
    name: 'Marcus Boyd',
    email: 'mboyd@safeguardproducts.com',
    role: 'support',
    org: 'SGI',
    scope: 'All stores',
    mfaEnabled: true,
    status: 'active',
    lastActiveAt: '3h ago',
  },
  {
    id: 'usr-007',
    name: 'Ingrid Lindqvist',
    email: 'ilindqvist@safeguardproducts.com',
    role: 'support',
    org: 'SGI',
    scope: 'All stores',
    mfaEnabled: true,
    status: 'active',
    lastActiveAt: '6h ago',
  },
  {
    id: 'usr-008',
    name: 'Susie Acosta Burton',
    email: 'sburton@lithia.com',
    role: 'read_only',
    org: 'Lithia',
    scope: 'All 13 stores',
    mfaEnabled: true,
    status: 'active',
    lastActiveAt: '1d ago',
  },
  {
    id: 'usr-009',
    name: 'Mike Fealey',
    email: 'mfealey@lithia.com',
    role: 'read_only',
    org: 'Lithia',
    scope: 'All 13 stores',
    mfaEnabled: true,
    status: 'active',
    lastActiveAt: '5d ago',
  },
  {
    id: 'usr-010',
    name: 'Courtney Toll',
    email: 'ctoll@lithia.com',
    role: 'read_only',
    org: 'Lithia',
    scope: 'Medford, Eugene',
    mfaEnabled: false,
    status: 'invited',
    lastActiveAt: null,
  },
  {
    id: 'usr-011',
    name: 'Grant Pierce',
    email: 'gpierce@safeguardproducts.com',
    role: 'operations_admin',
    org: 'SGI',
    scope: 'All stores',
    mfaEnabled: true,
    status: 'disabled',
    lastActiveAt: '46d ago',
  },
  {
    id: 'usr-012',
    name: 'Tyler Frost',
    email: 'tfrost@safeguardproducts.com',
    role: 'support',
    org: 'SGI',
    scope: 'All stores',
    mfaEnabled: true,
    status: 'invited',
    lastActiveAt: null,
  },
];

// ============== Admin System Logs mock data (ops-logs-001) ==============

const FILTER_TO_TYPES: Record<AdminAuditFilterKey, ReadonlyArray<string> | null> = {
  all: null,
  EXCEPTION_SUBMITTED: ['EXCEPTION_SUBMITTED'],
  ELIGIBILITY_CHECK: ['ELIGIBILITY_CHECK'],
  CASE_CREATED: ['CASE_CREATED'],
  DECISION_RECORDED: ['DECISION_RECORDED', 'EXCEPTION_APPROVED', 'EXCEPTION_DENIED'],
  VIN_COMMIT: ['VIN_COMMIT'],
  AUTH_SUCCESS: ['AUTH_SUCCESS'],
};

interface AuditSeed {
  readonly eventType: string;
  readonly hoursAgo: number;
  readonly actorName: string;
  readonly actorKind: AdminAuditEventRecord['actorKind'];
  readonly target: string;
  readonly detail: string;
}

const AUDIT_SEED: ReadonlyArray<AuditSeed> = [
  {
    eventType: 'AUTH_SUCCESS',
    hoursAgo: 0.1,
    actorName: 'Renee Foster',
    actorKind: 'admin',
    target: 'rfoster@safeguardproducts.com',
    detail: 'Okta SSO sign-in · IP 10.42.18.7',
  },
  {
    eventType: 'ADMIN_VIEW',
    hoursAgo: 0.2,
    actorName: 'Renee Foster',
    actorKind: 'admin',
    target: 'audit_events',
    detail: 'Viewed system logs · 42 results',
  },
  {
    eventType: 'EXCEPTION_SUBMITTED',
    hoursAgo: 0.5,
    actorName: 'Sandra Crewell',
    actorKind: 'consumer',
    target: 'EX-20471',
    detail: 'VIN 1HGCV1F38PA013477 · failure: contract not found',
  },
  {
    eventType: 'VIN_DECODE',
    hoursAgo: 0.6,
    actorName: 'Consumer Portal',
    actorKind: 'system',
    target: '1HGCV1F38PA013477',
    detail: '2023 Honda Accord · NHTSA decode succeeded',
  },
  {
    eventType: 'ELIGIBILITY_CHECK',
    hoursAgo: 0.7,
    actorName: 'Eligibility Engine',
    actorKind: 'system',
    target: 'EX-20471',
    detail: 'Result: NOT_FOUND · contract lookup miss',
  },
  {
    eventType: 'CASE_CREATED',
    hoursAgo: 0.9,
    actorName: 'Salesforce Bridge',
    actorKind: 'system',
    target: 'SF-CASE-885120',
    detail: 'Created from EX-20471 · queue: Secondary VIN Exceptions',
  },
  {
    eventType: 'LITHIA_NOTIFIED',
    hoursAgo: 1.0,
    actorName: 'Email Notifier',
    actorKind: 'system',
    target: 'dms-medford@lithia.com',
    detail: 'Sent Lithia DMS notification for EX-20471',
  },
  {
    eventType: 'DECISION_RECORDED',
    hoursAgo: 2.2,
    actorName: 'Priya Raman',
    actorKind: 'admin',
    target: 'EX-20438',
    detail: 'Decision: APPROVED · reviewed by operations_admin',
  },
  {
    eventType: 'ADMIN_VIEW',
    hoursAgo: 2.25,
    actorName: 'Priya Raman',
    actorKind: 'admin',
    target: 'EX-20438',
    detail: 'Viewed exception request detail',
  },
  {
    eventType: 'EXCEPTION_APPROVED',
    hoursAgo: 2.2,
    actorName: 'Priya Raman',
    actorKind: 'admin',
    target: 'EX-20438',
    detail: 'Decision: APPROVED · SF case closed',
  },
  {
    eventType: 'VIN_COMMIT',
    hoursAgo: 2.3,
    actorName: 'VIN Commit Worker',
    actorKind: 'system',
    target: 'CT-77821',
    detail: 'Committed VIN ****013488 to contract CT-77821',
  },
  {
    eventType: 'EXCEPTION_DENIED',
    hoursAgo: 5.4,
    actorName: 'Marcus Boyd',
    actorKind: 'admin',
    target: 'EX-20402',
    detail: 'Denial: Vehicle exceeds program limits · notice sent',
  },
  {
    eventType: 'LITHIA_SLA_ALERT',
    hoursAgo: 7.8,
    actorName: 'SLA Watcher',
    actorKind: 'system',
    target: 'EX-20381',
    detail: 'Lithia decision SLA breached at 48h · escalation sent',
  },
  {
    eventType: 'AUTH_SUCCESS',
    hoursAgo: 9.0,
    actorName: 'Wesley Cho',
    actorKind: 'admin',
    target: 'wcho@safeguardproducts.com',
    detail: 'Okta SSO sign-in · IP 10.42.18.41',
  },
  {
    eventType: 'EXCEPTION_SUBMITTED',
    hoursAgo: 12.5,
    actorName: 'Brett Kennedy',
    actorKind: 'consumer',
    target: 'EX-20366',
    detail: 'VIN 5YJ3E1EA8KF317442 · failure: VIN already committed',
  },
  {
    eventType: 'ELIGIBILITY_CHECK',
    hoursAgo: 12.6,
    actorName: 'Eligibility Engine',
    actorKind: 'system',
    target: 'EX-20366',
    detail: 'Result: DUPLICATE_VIN · prior commit found',
  },
  {
    eventType: 'CASE_CREATED',
    hoursAgo: 12.8,
    actorName: 'Salesforce Bridge',
    actorKind: 'system',
    target: 'SF-CASE-884901',
    detail: 'Created from EX-20366 · queue: Secondary VIN Exceptions',
  },
  {
    eventType: 'VIN_COMMIT',
    hoursAgo: 18.4,
    actorName: 'VIN Commit Worker',
    actorKind: 'system',
    target: 'CT-77512',
    detail: 'Committed VIN ****317442 to contract CT-77512',
  },
  {
    eventType: 'AUTH_SUCCESS',
    hoursAgo: 23.0,
    actorName: 'Dara Okafor',
    actorKind: 'admin',
    target: 'dokafor@safeguardproducts.com',
    detail: 'Okta SSO sign-in · IP 10.42.18.4',
  },
  {
    eventType: 'EXCEPTION_APPROVED',
    hoursAgo: 28.2,
    actorName: 'Wesley Cho',
    actorKind: 'admin',
    target: 'EX-20310',
    detail: 'Decision: APPROVED · DMS notified',
  },
];

function rangeWindowHours(range: string): number {
  if (range === '7') return 24 * 7;
  if (range === '90') return 24 * 90;
  if (range === 'custom') return 24 * 30;
  return 24 * 30;
}

function buildMockAuditEvents(params: {
  type: AdminAuditFilterKey;
  range: string;
  store: string;
  from?: string;
  to?: string;
}): AdminAuditEventListData {
  const cap = 250;
  const now = Date.now();
  const HOUR = 60 * 60 * 1000;
  const windowHours = rangeWindowHours(params.range);

  const storeFactor = params.store === 'all' ? 1 : 0.35;
  const totalReps = params.range === '7' ? 8 : params.range === '90' ? 30 : 14;
  const repeats = Math.max(1, Math.round(totalReps * storeFactor));

  const types = FILTER_TO_TYPES[params.type] ?? null;
  const filteredSeed = types ? AUDIT_SEED.filter((s) => types.includes(s.eventType)) : AUDIT_SEED;

  const events: AdminAuditEventRecord[] = [];
  let counter = 0;
  for (let r = 0; r < repeats; r++) {
    const offsetHours = r * (windowHours / Math.max(repeats, 1)) * 0.35;
    for (const seed of filteredSeed) {
      const totalHours = seed.hoursAgo + offsetHours;
      if (totalHours > windowHours) continue;
      counter++;
      events.push({
        id: `audit-${counter}-${seed.eventType.toLowerCase()}`,
        eventType: seed.eventType,
        createdAt: new Date(now - totalHours * HOUR).toISOString(),
        actorName: seed.actorName,
        actorKind: seed.actorKind,
        target: seed.target,
        detail: seed.detail,
      });
    }
  }

  events.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  const total = events.length;
  const shown = Math.min(total, cap);
  return {
    events: events.slice(0, cap),
    shown,
    total,
    rangeLabel: rangeLabelFor(params.range, params.from, params.to),
  };
}

function formatMockVehicleLabel(
  year: string | number | null | undefined,
  make: string | null | undefined,
  model: string | null | undefined,
): string | null {
  const yearPart = year != null && String(year).trim() !== '' ? String(year).trim() : '';
  const makePart = make?.trim() ?? '';
  const modelPart = model?.trim() ?? '';
  if (!yearPart || !makePart || !modelPart) {
    return null;
  }
  return `${yearPart} ${makePart.toUpperCase()} ${modelPart.toUpperCase()}`;
}
